import { useState } from 'react';
import { Collapse } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { HealthTierData, HealthAlert, AlertTier } from '../../../../types';
import { HealthAlertCard } from './HealthAlertCard';
import classes from './CohortHealthSummary.module.css';

interface HealthTierSectionProps {
  tier: AlertTier;
  data: HealthTierData;
  label: string;
  dotColor: string;
  defaultExpanded?: boolean;
  onDismiss?: (alert: HealthAlert) => void;
  onAddress?: (alert: HealthAlert) => void;
  onAddTask?: (alert: HealthAlert) => void;
  onViewEntity?: (alert: HealthAlert) => void;
  onDismissImprovement?: (alert: HealthAlert) => void;
  onRestore?: (alert: HealthAlert) => void;
  onViewNotes?: (alert: HealthAlert) => void;
}

export function HealthTierSection({
  tier,
  data,
  label,
  dotColor,
  defaultExpanded = false,
  onDismiss,
  onAddress,
  onAddTask,
  onViewEntity,
  onDismissImprovement,
  onRestore,
  onViewNotes,
}: HealthTierSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showResolved, setShowResolved] = useState(false);

  if (data.alerts.length === 0) return null;

  const activeAlerts = data.alerts.filter((a) => !a.isDismissed);
  const resolvedAlerts = tier !== 'notification' ? data.alerts.filter((a) => a.isDismissed) : [];
  const activeCount = activeAlerts.length;
  const resolvedCount = resolvedAlerts.length;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className={classes.tierSection}>
      <button
        type="button"
        className={classes.tierHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={classes.tierHeaderLeft}>
          <span className={classes.tierDot} style={{ backgroundColor: dotColor }} />
          <span className={classes.tierName}>{label}</span>
          <span className={classes.tierCount}>
            • {activeCount} {activeCount === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className={classes.tierHeaderRight}>
          {data.summary && (
            <span className={classes.tierSummary}>{data.summary}</span>
          )}
          <Chevron size={16} color="var(--mantine-color-gray-5)" />
        </div>
      </button>

      <Collapse in={expanded}>
        <div className={classes.alertCards}>
          {activeAlerts.map((alert) => (
            <HealthAlertCard
              key={alert.id}
              alert={alert}
              tier={tier}
              onDismiss={onDismiss}
              onAddress={onAddress}
              onAddTask={onAddTask}
              onViewEntity={onViewEntity}
              onDismissImprovement={onDismissImprovement}
            />
          ))}
          {showResolved &&
            resolvedAlerts.map((alert) => (
              <HealthAlertCard
                key={alert.id}
                alert={alert}
                tier={tier}
                onRestore={onRestore}
                onViewNotes={onViewNotes}
              />
            ))}
          {resolvedCount > 0 && (
            <div className={classes.resolvedToggle}>
              <button
                type="button"
                className={classes.resolvedToggleButton}
                onClick={() => setShowResolved(!showResolved)}
              >
                {showResolved
                  ? 'Hide Addressed & Dismissed'
                  : `Show Addressed & Dismissed (${resolvedCount})`}
              </button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}
